import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatDate } from "@/lib/api";
import { PageHeader, StatusPill, PriorityPill, EmptyState } from "@/components/shared/Common";
import { DateRangePicker } from "@/components/shared/DateRangePicker";
import { Pagination } from "@/components/shared/Pagination";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MagnifyingGlass, DownloadSimple, Plus } from "@phosphor-icons/react";
import { API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/contexts/PermissionsContext";

const STATUSES = ["pending", "assigned", "in_progress", "paused", "completed", "escalated", "rejected", "cancelled"];
const PRIORITIES = ["low", "medium", "high", "critical"];

export default function Cases() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const { can } = usePermissions();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("search") || "");
  const [status, setStatus] = useState(params.get("status") || "all");
  const [priority, setPriority] = useState(params.get("priority") || "all");
  const [dateRange, setDateRange] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (search) q.set("search", search);
      if (status !== "all") q.set("status", status);
      if (priority !== "all") q.set("priority", priority);
      if (dateRange?.from) q.set("date_from", dateRange.from);
      if (dateRange?.to) q.set("date_to", dateRange.to);
      q.set("limit", String(pageSize));
      q.set("skip", String((page - 1) * pageSize));
      const { data } = await api.get(`/cases?${q.toString()}`);
      setItems(data.items);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }, [search, status, priority, dateRange, page, pageSize]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, status, priority, dateRange, pageSize]);

  const canCreate = can("new_case") || user?.role !== "associate";

  return (
    <div>
      <PageHeader title="Cases" subtitle={`${total} case${total === 1 ? "" : "s"} across your view`}>
        <Button data-testid="cases-export-btn" variant="outline" className="h-9" onClick={() => window.open(`${API}/reports/export?type=cases`, "_blank")}>
          <DownloadSimple size={16} className="mr-1" /> Export CSV
        </Button>
        {canCreate && (
          <Button data-testid="cases-new-btn" className="h-9" onClick={() => nav("/cases/new")}>
            <Plus size={16} className="mr-1" /> New Case
          </Button>
        )}
      </PageHeader>

      <div className="border rounded-md bg-card">
        <div className="p-4 border-b flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-64">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="cases-search-input"
              placeholder="Search by case ID, customer, type…"
              className="pl-9 h-9"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="cases-status-filter" className="h-9 w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={priority} onValueChange={setPriority}>
            <SelectTrigger data-testid="cases-priority-filter" className="h-9 w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All priorities</SelectItem>
              {PRIORITIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <DateRangePicker testId="cases-date-range" value={dateRange} onChange={setDateRange} />
        </div>

        {loading && <div className="p-12 text-center text-sm text-muted-foreground">Loading cases…</div>}
        {!loading && items.length === 0 && <EmptyState title="No cases match your filters" />}
        {!loading && items.length > 0 && (
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-widest text-muted-foreground border-b">
                <tr>
                  <th className="px-4 py-3">Case</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Priority</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Assignee</th>
                  <th className="px-4 py-3">Due</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {items.map(c => (
                  <tr
                    key={c.id}
                    data-testid={`case-row-${c.case_id}`}
                    className="hover:bg-secondary/40 cursor-pointer"
                    onClick={() => nav(`/cases/${c.id}`)}
                  >
                    <td className="px-4 py-3 mono text-xs font-medium">{c.case_id}</td>
                    <td className="px-4 py-3">{c.customer}</td>
                    <td className="px-4 py-3 text-muted-foreground">{c.case_type}</td>
                    <td className="px-4 py-3"><PriorityPill priority={c.priority} /></td>
                    <td className="px-4 py-3"><StatusPill status={c.status} /></td>
                    <td className="px-4 py-3 text-muted-foreground">{c.assignee_name || <span className="text-amber-500">Unassigned</span>}</td>
                    <td className="px-4 py-3 text-xs mono text-muted-foreground">{formatDate(c.due_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          testId="cases-pagination"
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </div>
    </div>
  );
}
